import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { ArrowLeft, ShieldCheck, KeyRound, Loader2, AlertTriangle } from "lucide-react";

const OPS_DASHBOARD_URL = "https://ops-dashboard-136.emergent.host";

/**
 * Supervisão Operacional embed.
 *
 * Strategy:
 *   1. Call `/api/employee/ops-sso-token` to exchange the AVI session for a
 *      fresh Ops Dashboard JWT (only works when the admin has stored
 *      credentials for this supervisor).
 *   2. On success → load the iframe with `#sso_token=<jwt>` so the Ops app
 *      (small snippet that reads the hash) saves the token in its own
 *      `localStorage["token"]` and skips the login screen.
 *   3. On failure (no creds / wrong creds / Ops offline) → fall back to the
 *      plain iframe so the supervisor types user/pass manually.
 */
export default function OpsSupervisionView({ token, onBack }) {
  const [state, setState] = useState({ status: "loading", iframeSrc: null, message: "" });

  useEffect(() => {
    let cancelled = false;
    // Direct iframe to the AVI backend proxy that returns the Ops Dashboard
    // index.html with the supervisor's JWT pre-injected into localStorage.
    // This produces a true cross-origin auto-login because the iframe is
    // same-origin to AVI (so we can seed localStorage) while the bundle's
    // hardcoded API calls go cross-origin to OPS_DASHBOARD_URL (CORS allows
    // our preview domain).
    const probe = async () => {
      try {
        // Cheap availability check + early error reporting.
        const ping = await axios.get(`${API}/employee/ops-sso-token`, {
          params: { token },
          // We don't need the JWT itself — just to confirm SSO is configured
          // and the Ops backend is reachable.
        });
        if (cancelled) return;
        if (!ping.data?.token) {
          throw new Error("Sem token");
        }
        const bootstrapUrl = `${API}/employee/ops-sso-bootstrap?token=${encodeURIComponent(token)}`;
        setState({
          status: "sso",
          iframeSrc: bootstrapUrl,
          message: "",
        });
      } catch (e) {
        if (cancelled) return;
        const detail = e.response?.data?.detail || "";
        const status = e.response?.status;
        let msg = detail || "SSO indisponível — faça login manual.";
        if (status === 401) {
          msg = "Credenciais Ops inválidas — peça ao admin para atualizar.";
        } else if (status === 403) {
          msg = "Você não está cadastrado como supervisor.";
        } else if (status === 404) {
          msg = "Sem SSO cadastrado para você — peça ao admin para cadastrar usuário/senha.";
        } else if (status === 502) {
          msg = "Painel externo indisponível no momento.";
        }
        setState({
          status: "manual",
          iframeSrc: OPS_DASHBOARD_URL,
          message: msg,
        });
      }
    };
    probe();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <motion.div
      key="ops_supervision"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      data-testid="view-ops-supervision"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-4 -ml-2 text-slate-700 hover:bg-slate-100"
        data-testid="back-to-menu-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar ao menu
      </Button>

      <h2 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-indigo-600" />
        Supervisão Operacional
      </h2>

      {state.status === "loading" && (
        <div
          className="flex items-center gap-2 text-sm text-slate-600 mb-4"
          data-testid="ops-sso-loading"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Conectando ao painel…
        </div>
      )}

      {state.status === "manual" && (
        <div
          className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3"
          data-testid="ops-sso-fallback"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <span className="text-amber-800">{state.message}</span>
        </div>
      )}

      {state.status === "sso" && (
        <div
          className="flex items-center gap-2 text-xs text-emerald-700 mb-3"
          data-testid="ops-sso-ok"
        >
          <KeyRound className="w-3 h-3" />
          <span>Sessão iniciada automaticamente.</span>
        </div>
      )}

      {state.iframeSrc && (
        <div
          className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white"
          data-testid="ops-iframe-card"
        >
          <iframe
            src={state.iframeSrc}
            title="Painel de Supervisão Operacional"
            className="w-full block"
            style={{ height: "calc(100vh - 280px)", minHeight: "560px", border: 0 }}
            allow="clipboard-read; clipboard-write; camera; microphone; geolocation"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="ops-iframe"
          />
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-400 tracking-wide">
        Painel externo — AVI Ops · grupoavi.com.br
      </p>
    </motion.div>
  );
}
